import React, {useState} from 'react';
import {useNotificationContext} from "../../context/NotificationContext";
import {useGet} from "../../network/useGet";
import {postToHub} from "../../network/actions";
import NetworkWrapper from "../NetworkWrapper";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";
import {RadioButton} from "primereact/radiobutton";
import {InputSwitch} from "primereact/inputswitch";
import {Button} from "primereact/button";
import {numbersListRegex, numbersRangesListRegex} from "../../utils/validationUtils";


const messageTypes = [
    {label: "Urgent", value: "Urgent"},
    {label: "Important", value: "Important"},
    {label:"Normal", value: "Normal"},
];

function SendMessageForm(props) {
    const { toast } = useNotificationContext();
    const {isLoading: groupsLoading, data: groups, error: groupsError} = useGet("cabinmessage/groups");
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [type, setType] = useState("Normal");
    const [messageFor, setMessageFor] = useState("group");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [broadcastToAllDecks, setBroadcastToAllDecks] = useState(false);
    const [deckNumbers, setDeckNumbers] = useState("");
    const [cabinRanges, setCabinRanges] = useState("");
    const [cabinNumbers, setCabinNumbers] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});



    const handleGroupChange = (groupId) => {
        setSelectedGroup(groupId);
        const groupData = groups.find(group => group.id === groupId);

        if(groupData) {
            setBroadcastToAllDecks(groupData.broadcastToAllDecks);
            setDeckNumbers(groupData.deckNumbers);
            setCabinRanges(groupData.cabinRanges);
            setCabinNumbers(groupData.cabinNumbers);
        }
        else {
            setSelectedGroup("");
            setBroadcastToAllDecks(false);
            setDeckNumbers("");
            setCabinRanges("");
            setCabinNumbers("");
        }
    }

    const handleSubmit = async () => {
        //check if submitting
        if(submitting) {
            return;
        }

        // validate data
        const validationErrors = getValidationErrors();

        if(Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        //set submitting and clear any previous errors
        setSubmitting(true);
        setErrors({});

        // create the request & submit
        const payload = {
            messageContent: {
                values:{
                    en: content
                }
            },
            messageTitle:{
                values:{
                    en: title
                }
            },
            "broadcastToAllDecks" : broadcastToAllDecks,
            "messageType": type,
            "deckNumbers": deckNumbers,
            "cabinRanges": cabinRanges,
            "cabinNumbers": cabinNumbers,
        }

        const result = await postToHub("cabinmessage/send", payload);
        setSubmitting(false);


        if(result === "") {
            // close the form, trigger reload of main table & show toast notification
            toast.current.show({severity: 'success', summary: 'Message sent'});
            props.setReloadTriggerCount(prevCount => prevCount + 1);
            props.onClose();

        }
        else {
            // TODO - add the error handling
            console.log("result for creating a message was not as expected");
        }

    }

    const getValidationErrors = () => {
        const validationErrors = {};
        const requiredMessage = "This field is required, please fill it out";

        //title is required
        if(!title) {
            validationErrors.title = requiredMessage;
        }
        //content is required
        if(!content) {
            validationErrors.content = requiredMessage;
        }

        //if we have a broadcast to all decks, then no further validation is required as all other fields will be ignored
        if(broadcastToAllDecks) {
            return validationErrors;
        }

        //at least one of the cabin selectors/filters need to be present/active
        if( !deckNumbers && !cabinRanges && !cabinNumbers) {
            validationErrors.cabinList = true;
            validationErrors.missingConditions = "Please make sure that the numbers/ranges entered above are correct";
        }

        // deck numbers are optional but can only contain letters, space and comma
        if(deckNumbers && !numbersListRegex.test(deckNumbers)) {
            validationErrors.cabinList = true;
            validationErrors.deckNumbers = "Please use only numbers, spaces and commas for deck numbers";
        }

        // cabin numbers are optional but can only contain letters, space and comma
        if(cabinNumbers && !numbersListRegex.test(cabinNumbers)) {
            validationErrors.cabinList = true;
            validationErrors.cabinNumbers = "Please use only numbers, spaces and commas for cabin numbers";
        }

        // cabin ranges are optional but need to be in format number-number (and can be repeated when separated by commas
        if(cabinRanges && !numbersRangesListRegex.test(cabinRanges)) {
            validationErrors.cabinList = true;
            validationErrors.cabinRanges = "Please make sure cabin ranges are comma separated and format: number - number";
        }

        return validationErrors;
    }

// TODO - extract the fields used to more generic components to be used throught the project
// similar to what we already have in Fields.js, but for individual use with own states not bundled to a form
    return (
        <NetworkWrapper isLoading={groupsLoading} error={groupsError}>
            <div className="my-4">
                <label> Message title
                    <InputText className="block w-full"
                               value={title}
                               onChange={(e) => setTitle(e.target.value)}/>
                </label>
                {errors.title &&
                    <div className="text-red-700">
                        {errors.title}
                    </div>
                }
            </div>

            <div className="my-4">
                <label> Message content
                    <InputTextarea className="block w-full"
                                   value={content}
                                   onChange={(e) => setContent(e.target.value)}/>
                </label>
                {errors.content &&
                    <div className="text-red-700">
                        {errors.content}
                    </div>
                }
            </div>


            <div className="my-4">
                <label className="block" htmlFor="message-type-dropdown">Message type</label>
                <Dropdown
                    inputId="message-type-dropdown"
                    value={type}
                    options={messageTypes}
                    onChange={(e) => setType(e.target.value)}
                    placeholder="Please select"
                    className="w-full"/>
            </div>

            <div className="my-8">
                <div>
                    Who is this message for:
                </div>
                <div className="flex justify-between">
                    <div className="">
                        <label>
                            <RadioButton name="messagefor" value="group" onChange={(e) => {handleGroupChange(""); setMessageFor(e.value)}} checked={messageFor === 'group'} />
                            <span className="ml-2">
                                Predefined group
                            </span>
                        </label>
                    </div>
                    <div className="">
                        <label className="ml-2">
                            <RadioButton name="messagefor" value="custom" onChange={(e) => setMessageFor(e.value)} checked={messageFor === 'custom'} />
                            <span className="ml-2">
                                Custom
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {messageFor === "group" &&
            <div className="mt-4">
                <label className="block" htmlFor="group-dropdown">Group</label>
                <Dropdown
                    inputId="group-dropdown"
                    value={selectedGroup}
                    options={groups}
                    onChange={(e) => handleGroupChange(e.target.value)}
                    optionLabel="groupName.values.en"
                    optionValue="id"
                    placeholder="Select a group"
                    className="w-full"/>

                <div className="mt-4">
                    <div className="mt-1">
                        Broadcast to all decks: {broadcastToAllDecks ? "YES" : "NO"}
                    </div>
                    <div className="mt-1">
                        Deck numbers: {deckNumbers}
                    </div>
                    <div className="mt-1">
                        Cabin ranges: {cabinRanges}
                    </div>
                    <div className="mt-1">
                        Cabin numbers: {cabinNumbers}
                    </div>
                </div>
                { errors.cabinList &&
                    <div className="text-red-700 mt-2">
                        Please make sure that the selected group has valid parameters set up under: Messaging -> Groups
                    </div>
                }
            </div>
            }

            {messageFor === "custom" &&
            <div className="mt-4">
                <div className="my-4">
                    <label className="block" htmlFor="broadcast-all-switch">Send message to ALL cabins?</label>

                    <InputSwitch
                        id="broadcast-all-switch"
                        checked={broadcastToAllDecks}
                        onChange={() => setBroadcastToAllDecks(prevBroadcastToAllDecks => !prevBroadcastToAllDecks)} />
                </div>
                { !broadcastToAllDecks &&
                <>
                    <div className="my-4">
                        <label> Deck numbers
                            <InputText className="block w-full"
                                       value={deckNumbers}
                                       onChange={(e) => setDeckNumbers(e.target.value)}/>
                        </label>
                        {errors.deckNumbers &&
                            <div className="text-red-700">
                                {errors.deckNumbers}
                            </div>
                        }
                    </div>

                    <div className="my-4">
                        <label> Cabin ranges
                            <InputText className="block w-full"
                                       value={cabinRanges}
                                       onChange={(e) => setCabinRanges(e.target.value)}/>
                        </label>
                        {errors.cabinRanges &&
                            <div className="text-red-700">
                                {errors.cabinRanges}
                            </div>
                        }
                    </div>

                    <div className="my-4">
                        <label> Cabin numbers
                            <InputText className="block w-full"
                                       value={cabinNumbers}
                                       onChange={(e) => setCabinNumbers(e.target.value)}/>
                        </label>
                        {errors.cabinNumbers &&
                            <div className="text-red-700">
                                {errors.cabinNumbers}
                            </div>
                        }
                    </div>

                    {errors.missingConditions &&
                        <div className="my-4 text-red-700">
                            {errors.missingConditions}
                        </div>
                    }
                </>
                }

            </div>
            }


            <div className="mt-8 flex justify-between">
                <Button label="Send" onClick={handleSubmit}/>
                <Button label="Close" onClick={props.onClose}/>
            </div>

        </NetworkWrapper>
    )
}
export default SendMessageForm;